<template>
  <div class="header-wrapper">
    <!-- 1. Верхний оранжевый промо-бар -->
    <div v-if="showPromoBar" class="top-promo-bar">
      <div class="promo-container">
        <span class="promo-text">
          Получи 300 бонусов в приложении с первого заказа. Да, мы стали еще ближе
        </span>
        <button class="promo-btn" type="button" @click="openAppDownloadModal">
          Скачать
        </button>
      </div>
    </div>

    <!-- 2. Основная шапка (десктопная) -->
    <header class="main-header">
      <div class="header-container">
        <!-- Логотип -->
        <router-link to="/menu" class="brand-link" aria-label="Maxi Rolls – главное меню">
          <span class="brand-text">MAXI ROLLS</span>
        </router-link>

        <!-- Город и телефон -->
        <div class="city-phone-block">
          <div class="city-line">
            <span class="city-label">Ваш город </span>
            <button class="city-select-btn" type="button" @click="changeCity">
              <span class="city-name">{{ currentCity || 'Ижевск' }}</span>
              <i class="bi bi-caret-down-fill city-caret"></i>
            </button>
          </div>
          <a :href="'tel:' + cityPhoneClean" class="phone-link">
            {{ cityPhone }}
          </a>
        </div>

        <!-- Кнопка выбора адреса доставки -->
        <button class="address-pill-btn" type="button" @click="openAddressModal">
          <i class="bi bi-building address-icon"></i>
          <span class="address-text">{{ deliveryAddressText }}</span>
          <i class="bi bi-chevron-right address-arrow"></i>
        </button>

        <!-- Кнопка входа / Профиль -->
        <div class="header-auth">
          <button
            v-if="!isAuthenticated"
            class="auth-pill-btn"
            type="button"
            @click="goToLogin"
          >
            ВОЙТИ
          </button>
          <button
            v-else
            class="auth-pill-btn auth-pill-btn--logged"
            type="button"
            @click="goToSettings"
          >
            <i class="bi bi-person-fill"></i>
            <span>{{ userFirstName }}</span>
          </button>
        </div>
      </div>
    </header>

    <!-- 3. Прилипающая панель категорий и корзины (Sticky Category Bar) -->
    <nav 
      class="sticky-nav-bar"
      :class="{ 'is-stuck': isScrolled }"
      aria-label="Категории меню"
    >
      <div class="nav-container">
        <!-- Мини-логотип (появляется при скролле) -->
        <router-link 
          to="/menu" 
          class="sticky-brand"
          :class="{ 'visible': isScrolled }"
          aria-label="Maxi Rolls"
        >
          MAXI ROLLS
        </router-link>

        <!-- Список категорий -->
        <div class="categories-track" ref="categoriesTrackRef">
          <button
            v-for="cat in navCategories"
            :key="cat.id"
            @click="handleCategoryClick(cat)"
            class="category-tab"
            :class="{ 'active': activeCategoryId === cat.id }"
            type="button"
          >
            {{ cat.name.toUpperCase() }}
          </button>

          <!-- Информационные ссылки -->
          <router-link to="/promotions" class="category-tab category-tab--link">
            АКЦИИ
          </router-link>
          <button class="category-tab category-tab--link" type="button" @click="openInfoModal('delivery')">
            ДОСТАВКА И ОПЛАТА
          </button>
          <button class="category-tab category-tab--link" type="button" @click="openInfoModal('franchise')">
            ФРАНШИЗА
          </button>
        </div>

        <!-- Правая часть: разделитель, поиск, сортировка, корзина -->
        <div class="nav-actions">
          <div class="nav-divider" aria-hidden="true"></div>
          <button 
            class="nav-icon-btn" 
            type="button" 
            aria-label="Поиск по блюдам"
            @click="goToSearch"
          >
            <i class="bi bi-search"></i>
          </button>
          <button 
            class="nav-icon-btn" 
            type="button" 
            aria-label="Сортировка"
            @click="toggleSort"
          >
            <i class="bi bi-arrow-down-up"></i>
          </button>

          <!-- Яркая оранжевая кнопка "КОРЗИНА" -->
          <button class="nav-cart-btn" type="button" @click="goToCart">
            <span class="cart-btn-text">КОРЗИНА</span>
            <span v-if="cartItemsCount > 0" class="cart-count-badge">
              {{ cartItemsCount }}
            </span>
          </button>
        </div>
      </div>
    </nav>

    <!-- Модальное окно "Скачать приложение" -->
    <div v-if="showAppModal" class="modal-backdrop" @click="showAppModal = false">
      <div class="modal-card" @click.stop>
        <button class="modal-close" type="button" @click="showAppModal = false">×</button>
        <h3 class="modal-title">Мобильное приложение Maxi Rolls</h3>
        <p class="modal-desc">
          Установите приложение и получите 300 бонусов на первый заказ!
        </p>
        <div class="app-links">
          <a href="https://apps.apple.com" target="_blank" rel="noopener" class="app-badge-btn">
            <i class="bi bi-apple"></i>
            <div>
              <small>Загрузите в</small>
              <strong>App Store</strong>
            </div>
          </a>
          <a href="https://play.google.com" target="_blank" rel="noopener" class="app-badge-btn">
            <i class="bi bi-google-play"></i>
            <div>
              <small>Доступно в</small>
              <strong>Google Play</strong>
            </div>
          </a>
        </div>
      </div>
    </div>

    <!-- Модальное окно "Доставка и оплата" / "Франшиза" -->
    <div v-if="infoModalType" class="modal-backdrop" @click="infoModalType = null">
      <div class="modal-card" @click.stop>
        <button class="modal-close" type="button" @click="infoModalType = null">×</button>
        <h3 class="modal-title">{{ infoModalTitle }}</h3>
        <div class="modal-body-text" v-html="infoModalContent"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCartStore } from '@/stores/cart'
import { useMenuStore } from '@/stores/menu'
import { storage } from '@/composables/useStorage'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const cartStore = useCartStore()
const menuStore = useMenuStore()

const currentCity = ref('Ижевск')
const showPromoBar = ref(true)
const showAppModal = ref(false)
const infoModalType = ref(null)
const isScrolled = ref(false)
const activeCategoryId = ref(null)
const categoriesTrackRef = ref(null)

// Городские телефоны
const cityPhoneMap = {
  'Ижевск': '+7 (922) 514-33-10',
  'Сарапул': '+7 (922) 514-33-10',
  'Нефтекамск': '+7 (937) 840-00-50',
  'Лениногорск': '+7 (937) 580-00-40',
  'Белебей': '+7 (937) 840-00-60',
  'Елабуга': '+7 (937) 580-00-70',
  'Азнакаево': '+7 (937) 580-00-80',
  'Бугульма': '+7 (937) 580-00-90'
}

const cityPhone = computed(() => {
  return cityPhoneMap[currentCity.value] || '+7 (922) 514-33-10'
})

const cityPhoneClean = computed(() => {
  return cityPhone.value.replace(/\D/g, '')
})

const deliveryAddressText = computed(() => {
  const saved = storage.get('selected_delivery_address')
  if (saved && typeof saved === 'string' && saved.trim()) {
    return saved.trim()
  }
  return 'Выберите адрес'
})

const isAuthenticated = computed(() => authStore.isAuthenticated)
const userFirstName = computed(() => authStore.currentUser?.first_name || 'Профиль')
const cartItemsCount = computed(() => cartStore.itemCount || 0)

// Категории для навигации
const navCategories = computed(() => {
  return (menuStore.categories || []).filter(c => {
    // Показываем только категории, в которых есть товары
    if (!menuStore.productsByCategory) return true
    const items = menuStore.productsByCategory[c.id] || []
    return items.length > 0
  })
})

const syncCity = () => {
  currentCity.value = authStore.currentUser?.city || storage.get('selected_city') || 'Ижевск'
}

const handleScroll = () => {
  isScrolled.value = window.scrollY > 100

  // Определение активной категории при скролле
  if (route.path === '/menu' || route.path === '/') {
    const sections = document.querySelectorAll('[data-category-section]')
    let found = null
    sections.forEach((sec) => {
      const rect = sec.getBoundingClientRect()
      if (rect.top <= 140 && rect.bottom >= 140) {
        found = sec.getAttribute('data-category-section')
      }
    })
    if (found) {
      activeCategoryId.value = found
    }
  }
}

const handleCategoryClick = (cat) => {
  activeCategoryId.value = cat.id
  if (route.path !== '/menu' && route.path !== '/') {
    router.push({ path: '/menu', query: { cat: cat.id } })
    return
  }
  
  const el = document.querySelector(`[data-category-section="${cat.id}"]`)
  if (el) {
    const headerOffset = 110
    const elementPosition = el.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    })
  }
}

const changeCity = () => router.push('/city')
const goToLogin = () => router.push('/auth')
const goToSettings = () => router.push('/settings')
const goToCart = () => router.push('/cart')
const goToSearch = () => router.push('/search')

const toggleSort = () => {
  // Переключение сортировки цены в MenuPage
  window.dispatchEvent(new CustomEvent('toggle-catalog-sort'))
}

const openAddressModal = () => {
  router.push('/checkout')
}

const openAppDownloadModal = () => {
  showAppModal.value = true
}

const dismissPromoBar = () => {
  showPromoBar.value = false
}

const openInfoModal = (type) => {
  infoModalType.value = type
}

const infoModalTitle = computed(() => {
  if (infoModalType.value === 'delivery') return 'Доставка и оплата'
  if (infoModalType.value === 'franchise') return 'Франшиза Maxi Rolls'
  return ''
})

const infoModalContent = computed(() => {
  if (infoModalType.value === 'delivery') {
    return `
      <p><strong>Быстрая доставка от 45 минут!</strong></p>
      <p>Минимальная сумма заказа зависит от района доставки. При заказе от 700 ₽ доставка бесплатная.</p>
      <p><strong>Способы оплаты:</strong></p>
      <ul>
        <li>Онлайн-оплата на сайте / в приложении</li>
        <li>Банковской картой курьеру</li>
        <li>Наличными при получении</li>
      </ul>
    `
  }
  if (infoModalType.value === 'franchise') {
    return `
      <p><strong>Откройте свой ресторан Maxi Rolls в вашем городе!</strong></p>
      <p>Проверенная бизнес-модель, окупаемость от 9 месяцев, полная поддержка на всех этапах запуска и операционной деятельности.</p>
      <p>Для связи с отделом франчайзинга: <a href="tel:+79225143310" style="color: #ff4b00; font-weight: bold;">+7 (922) 514-33-10</a></p>
    `
  }
  return ''
})

onMounted(() => {
  syncCity()
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

watch(() => route.fullPath, syncCity)

watch(navCategories, (cats) => {
  if (!activeCategoryId.value && cats && cats.length > 0) {
    activeCategoryId.value = cats[0].id
  }
}, { immediate: true })
</script>

<style scoped>
.header-wrapper {
  width: 100%;
  background: #ffffff;
  position: relative;
  z-index: 1000;
}

/* 1. Верхний промо-бар */
.top-promo-bar {
  background: #FF4200;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 16px;
  width: 100%;
}

.promo-container {
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  position: relative;
}

.promo-text {
  text-align: center;
  line-height: 1.3;
}

.promo-btn {
  background: #ffffff;
  color: #FF4200;
  border: none;
  border-radius: 9999px;
  padding: 5px 16px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s;
}

.promo-btn:hover {
  opacity: 0.92;
}

/* 2. Основная шапка */
.main-header {
  border-bottom: 1px solid #f0ede8;
  background: #ffffff;
}

.header-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  gap: 28px;
}

/* Логотип */
.brand-link {
  text-decoration: none;
  display: flex;
  align-items: center;
}

.brand-text {
  font-size: 24px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a1a;
  text-transform: uppercase;
  white-space: nowrap;
  user-select: none;
}

/* Город и телефон */
.city-phone-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 140px;
}

.city-line {
  font-size: 12px;
  color: #777777;
  display: flex;
  align-items: center;
  gap: 4px;
}

.city-select-btn {
  background: transparent;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 700;
  color: #FF4200;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.city-caret {
  font-size: 9px;
  color: #FF4200;
}

.phone-link {
  font-size: 14px;
  font-weight: 700;
  color: #222222;
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.15s;
}

.phone-link:hover {
  color: #FF4200;
}

/* Адрес доставки */
.address-pill-btn {
  flex: 1;
  max-width: 380px;
  height: 42px;
  background: #ffffff;
  border: 1px solid #ded8cf;
  border-radius: 9999px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #555555;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.address-pill-btn:hover {
  border-color: #FF4200;
  box-shadow: 0 2px 10px rgba(255, 66, 0, 0.08);
}

.address-icon {
  font-size: 15px;
  color: #777777;
}

.address-text {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.address-arrow {
  font-size: 12px;
  color: #999999;
}

/* Кнопка "ВОЙТИ" */
.header-auth {
  margin-left: auto;
}

.auth-pill-btn {
  height: 40px;
  padding: 0 22px;
  background: #ffffff;
  border: 1.5px solid #FF4200;
  border-radius: 9999px;
  color: #FF4200;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.16s cubic-bezier(0.16, 1, 0.3, 1);
  white-space: nowrap;
  user-select: none;
}

.auth-pill-btn:hover {
  background: #FF4200;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(255, 66, 0, 0.25);
}

.auth-pill-btn:active {
  transform: scale(0.96);
}

.auth-pill-btn--logged {
  border-color: #ded8cf;
  color: #1a1a1a;
  text-transform: none;
  font-size: 13px;
}

.auth-pill-btn--logged:hover {
  border-color: #FF4200;
  color: #FF4200;
  background: #ffffff;
  box-shadow: none;
}

/* 3. Прилипающая панель категорий (Sticky Category Bar) */
.sticky-nav-bar {
  width: 100%;
  background: #ffffff;
  position: sticky;
  top: 0;
  z-index: 999;
  border-bottom: 1px solid #f0ede8;
  transition: box-shadow 0.2s ease;
}

.sticky-nav-bar.is-stuck {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.nav-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  height: 56px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.sticky-brand {
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #1a1a1a;
  text-decoration: none;
  display: none;
  white-space: nowrap;
}

.sticky-brand.visible {
  display: block;
}

.categories-track {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  flex: 1;
  height: 100%;
}

.categories-track::-webkit-scrollbar {
  display: none;
}

.category-tab {
  background: transparent;
  border: none;
  padding: 0 12px;
  height: 100%;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #222222;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  position: relative;
  text-decoration: none;
  transition: color 0.15s ease;
}

.category-tab:hover {
  color: #FF4200;
}

.category-tab.active {
  color: #FF4200;
  font-weight: 800;
}

.category-tab--link {
  color: #555555;
  font-weight: 700;
}

/* Правые кнопки панели */
.nav-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}

.nav-divider {
  width: 1px;
  height: 24px;
  background-color: #e2e2e8;
  margin: 0 4px;
  flex-shrink: 0;
}

.nav-icon-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  background: #f4f2ee;
  color: #555555;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, transform 0.12s cubic-bezier(0.16, 1, 0.3, 1);
  user-select: none;
}

.nav-icon-btn:hover {
  background: #e8e4dc;
  color: #1a1a1a;
}

.nav-icon-btn:active {
  transform: scale(0.90);
}

.nav-icon-btn i {
  font-size: 15px;
}

/* Кнопка "КОРЗИНА" */
.nav-cart-btn {
  height: 42px;
  padding: 0 24px;
  background: #FF4200;
  border: none;
  border-radius: 9999px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background 0.16s ease, transform 0.15s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.16s ease;
  white-space: nowrap;
  user-select: none;
}

.nav-cart-btn:hover {
  background: #e03b00;
  box-shadow: 0 4px 14px rgba(255, 66, 0, 0.35);
  transform: translateY(-1px);
}

.nav-cart-btn:active {
  transform: scale(0.96);
}

.cart-count-badge {
  background: #ffffff;
  color: #FF4200;
  font-size: 11px;
  font-weight: 800;
  border-radius: 9999px;
  padding: 1px 7px;
  line-height: 1.4;
  animation: badgeBump 0.28s cubic-bezier(0.16, 1, 0.3, 1);
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
}

/* Модальные окна */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.modal-card {
  width: 100%;
  max-width: 460px;
  background: #ffffff;
  border-radius: 20px;
  padding: 30px;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: modalIn 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes modalIn {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(6px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-close {
  position: absolute;
  top: 14px;
  right: 18px;
  border: none;
  background: transparent;
  font-size: 26px;
  color: #888888;
  cursor: pointer;
  line-height: 1;
}

.modal-title {
  font-size: 20px;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 12px;
}

.modal-desc {
  font-size: 14px;
  color: #666666;
  margin-bottom: 24px;
  line-height: 1.45;
}

.app-links {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.app-badge-btn {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 18px;
  border-radius: 12px;
  background: #1a1a1a;
  color: #ffffff;
  text-decoration: none;
  transition: opacity 0.2s;
}

.app-badge-btn:hover {
  opacity: 0.9;
}

.app-badge-btn i {
  font-size: 26px;
}

.app-badge-btn div {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.app-badge-btn small {
  font-size: 10px;
  color: #aaaaaa;
}

.app-badge-btn strong {
  font-size: 15px;
}

.modal-body-text {
  font-size: 14px;
  color: #444444;
  line-height: 1.6;
}

@media (max-width: 1024px) {
  .header-container {
    padding: 10px 16px;
    gap: 16px;
  }

  .address-pill-btn {
    display: none;
  }

  .nav-container {
    padding: 0 16px;
  }
}

@media (max-width: 640px) {
  .top-promo-bar {
    font-size: 11px;
    padding: 6px 12px;
  }
  
  .promo-text {
    font-size: 11px;
  }

  .promo-btn {
    padding: 3px 10px;
    font-size: 10px;
  }

  .brand-text {
    font-size: 20px;
  }

  .city-phone-block {
    min-width: auto;
  }

  .phone-link {
    display: none;
  }

  .auth-pill-btn {
    height: 32px;
    padding: 0 12px;
    font-size: 11px;
  }

  .nav-cart-btn {
    height: 34px;
    padding: 0 12px;
    font-size: 11px;
  }

  .cart-btn-text {
    display: none;
  }
  
  .nav-cart-btn::before {
    content: '🛒';
  }
}
</style>
